/**
 * Rebuy <> theme cart bridge
 *
 * Two-way sync between Rebuy (Smart Cart / widgets) and the Shapes cart:
 *
 *   Rebuy -> theme  Cart changes made inside Smart Cart re-render the theme's
 *                   cart sections (cart-icon, cart-item-count, cart-items,
 *                   cart-footer, cart-live-region) through `theme:update:cart`.
 *   theme -> Rebuy  Add to cart / quantity changes made by the theme are pushed
 *                   into Rebuy so Smart Cart never shows a stale cart.
 *
 * It also guards the cart icons. `<html data-rebuy-cart>` houdt de stand bij:
 *
 *   pending      Smart Cart is onderweg. De iconen zijn via CSS niet klikbaar en
 *                deze bridge slikt wat er via toetsenbord alsnog doorheen komt.
 *                Niemand belandt op de /cart-pagina.
 *   ready        Een klik opent Smart Cart. Een klik die tijdens `pending` viel
 *                wordt alsnog gehonoreerd, mits hij niet te oud is.
 *   unavailable  Smart Cart komt niet. We laten de iconen met rust en het thema
 *                doet weer zijn eigen ding — we navigeren nooit uit onszelf.
 *
 * De eerste click capture staat inline in `snippets/rebuy-cart-bridge.liquid`,
 * zodat hij al actief is voordat dit (deferred) bestand draait; hier nemen we
 * het over zodra we geladen zijn.
 *
 * Dat Smart Cart er überhaupt komt is op deze shop niet vanzelfsprekend: zie
 * `assets/rebuy-smartcart-recovery.js`, dat Rebuy's eigen mislukte module-import
 * repareert. Zolang die bezig is (`config.recovery`) blijven we wachten.
 *
 * Rebuy events: https://developers.rebuyengine.com/reference/smart-cart-event-listeners/
 * Theme events: https://help.switchthemes.co/shapes/developer/theme-events/overview.html
 */
(function () {
  const config = window.theme && window.theme.rebuy;

  if (!config || !config.enabled) return;

  /** Collapse bursts of Rebuy events into a single section render. */
  const SYNC_DEBOUNCE = 150;

  /** Ignore Rebuy cart events caused by our own theme -> Rebuy sync. */
  const SUPPRESS_WINDOW = 1000;

  /**
   * Collapse bursts of theme cart changes into one Rebuy sync. Quantity steppers
   * (quick order list) fire several changes in a row, and each sync is a
   * `/cart.js` round trip plus a Smart Cart re-render.
   */
  const REBUY_SYNC_DEBOUNCE = 250;

  /** Poll interval while waiting for Smart Cart to come up. */
  const READY_POLL = 100;

  /**
   * How long to keep waiting for `Rebuy.SmartCart` after `window.Rebuy` itself
   * has loaded. Stores that run Rebuy without Smart Cart never get a Smart Cart
   * object, and we don't want those clicks to hang for the full timeout.
   */
  const SMART_CART_GRACE = 800;

  /**
   * Bovengrens op het wachten, ook als de Smart Cart recovery nog bezig is. Pas
   * hierna staat vast dat Smart Cart er niet komt.
   */
  const HARD_READY_LIMIT = 18000;

  /**
   * Een klik die moest wachten opent de cart alsnog — maar niet als Smart Cart
   * er zo lang over deed dat de klant allang iets anders doet.
   */
  const QUEUED_OPEN_MAX_WAIT = 6000;

  let themeUpdateTimer = null;
  let suppressRebuyUntil = 0;

  /* -------------------------------------------------- Rebuy helpers */

  const smartCart = () => (window.Rebuy && window.Rebuy.SmartCart) || null;
  const rebuyCart = () => (window.Rebuy && window.Rebuy.Cart) || null;

  /**
   * `rebuy:smartcart.ready` kan al gevallen zijn voordat dit bestand draaide, dus
   * we vertrouwen niet alleen op die vlag: Smart Cart zet zelf `status` op
   * 'ready' zodra hij staat. (`initialized` is de oude v1-variant.)
   */
  const isSmartCartReady = () => {
    const cart = smartCart();
    if (!cart || typeof cart.show !== 'function') return false;

    return (
      config.ready === true ||
      cart.status === 'ready' ||
      cart.initialized === true
    );
  };

  const openSmartCart = () => {
    const cart = smartCart();
    if (!cart || typeof cart.show !== 'function') return false;

    cart.show();
    return true;
  };

  document.addEventListener('rebuy:smartcart.ready', () => {
    config.ready = true;
    config.setState('ready');
  });

  /* -------------------------------------------------- Rebuy -> theme */

  const updateThemeCart = () => {
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
  };

  const scheduleThemeCartUpdate = (event) => {
    // Skip the cart change we dispatched ourselves, plus anything Rebuy fires
    // while catching up with a cart the theme already re-rendered.
    if (event && event.detail && event.detail.themeSync) return;
    if (Date.now() < suppressRebuyUntil) return;

    clearTimeout(themeUpdateTimer);
    themeUpdateTimer = setTimeout(updateThemeCart, SYNC_DEBOUNCE);
  };

  [
    'rebuy:cart.add',
    'rebuy:cart.change',
    'rebuy:smartcart.line-item-increase',
    'rebuy:smartcart.line-item-decrease',
    'rebuy:smartcart.line-item-removed',
    'rebuy:smartcart.item-switch-to-subscription',
    'rebuy:smartcart.item-switch-to-one-time',
  ].forEach((eventName) => {
    document.addEventListener(eventName, scheduleThemeCartUpdate);
  });

  /* -------------------------------------------------- theme -> Rebuy */

  const syncRebuyFromShopify = () => {
    const cart = rebuyCart();
    if (!cart || typeof cart.fetchShopifyCart !== 'function') return;

    suppressRebuyUntil = Date.now() + SUPPRESS_WINDOW;

    cart.fetchShopifyCart(() => {
      const instance = smartCart();

      if (instance) {
        if (typeof instance.updateCartCount === 'function') {
          instance.updateCartCount();
        }
        if (typeof instance.updateCartSubtotal === 'function') {
          instance.updateCartSubtotal();
        }
      }

      // Let Smart Cart and the Rebuy widgets re-render against the cart the
      // theme just changed. Flagged so it doesn't bounce back to us.
      document.dispatchEvent(
        new CustomEvent('rebuy:cart.change', {
          detail: { cart, themeSync: true },
        })
      );
    });
  };

  let rebuySyncTimer = null;

  const scheduleRebuySync = () => {
    clearTimeout(rebuySyncTimer);
    rebuySyncTimer = setTimeout(syncRebuyFromShopify, REBUY_SYNC_DEBOUNCE);
  };

  document.addEventListener('theme:product:add', () => {
    clearTimeout(rebuySyncTimer);
    syncRebuyFromShopify();

    if (config.openOnAdd) openWhenReady();
  });

  document.addEventListener('theme:line-item:change', scheduleRebuySync);

  // `theme:line-item:change` is only dispatched when the theme's cart items
  // component is on the page (cart drawer / cart page). These lower level events
  // also fire for the quick order list on product and collection pages, so
  // Smart Cart doesn't go stale there. The debounce collapses the overlap.
  window.addEventListener('shapes:cart:cartqtychange', scheduleRebuySync);
  window.addEventListener('shapes:cart:afteradditem', scheduleRebuySync);

  /* -------------------------------------------------- Cart link guard */

  /**
   * Resolves once we know whether Smart Cart is going to show up. Shared by
   * every caller, so er draait maar één poll per pagina.
   */
  let readyPromise = null;

  const recoveryPending = () =>
    config.recovery === 'waiting' || config.recovery === 'running';

  const whenReady = () => {
    if (readyPromise) return readyPromise;

    readyPromise = new Promise((resolve) => {
      if (isSmartCartReady()) {
        resolve(true);
        return;
      }

      const startedAt = Date.now();
      let rebuyLoadedAt = null;

      const finish = (ready) => {
        clearInterval(poll);
        document.removeEventListener('rebuy:smartcart.ready', check);
        resolve(ready);
      };

      function check() {
        if (isSmartCartReady()) {
          finish(true);
          return;
        }

        const waited = Date.now() - startedAt;

        if (waited > HARD_READY_LIMIT) {
          finish(false);
          return;
        }

        // De recovery is nog bezig Rebuy's mislukte import te repareren.
        if (recoveryPending()) return;

        if (!window.Rebuy) {
          if (waited > config.readyTimeout) finish(false);
          return;
        }

        if (rebuyLoadedAt === null) rebuyLoadedAt = Date.now();

        // Shops zonder Smart Cart krijgen nooit een SmartCart-object; daar
        // hoeven we de volle timeout niet voor uit te zitten.
        if (!smartCart() && Date.now() - rebuyLoadedAt > SMART_CART_GRACE) {
          finish(false);
          return;
        }

        if (waited > config.readyTimeout) finish(false);
      }

      const poll = setInterval(check, READY_POLL);

      document.addEventListener('rebuy:smartcart.ready', check);
      check();
    });

    return readyPromise;
  };

  /**
   * Opens Smart Cart, waiting for it when it hasn't loaded yet. Er wordt nooit
   * genavigeerd: zolang Smart Cart onderweg is zijn de cart-iconen via CSS niet
   * klikbaar, en komt Smart Cart er niet, dan geven we de iconen terug aan het
   * thema in plaats van de klant op de /cart-pagina te zetten.
   */
  const openWhenReady = () => {
    if (isSmartCartReady() && openSmartCart()) return;

    const clickedAt = Date.now();

    whenReady().then((ready) => {
      if (!ready) return;
      if (Date.now() - clickedAt > QUEUED_OPEN_MAX_WAIT) return;

      openSmartCart();
    });
  };

  // Zet de paginastatus zodra die vaststaat, los van of er geklikt is.
  whenReady().then((ready) => {
    config.setState(ready ? 'ready' : 'unavailable');
  });

  config.onCartClick = (event) => {
    if (config.state() === 'unavailable') return;
    if (!config.findCartTrigger(event)) return;

    event.preventDefault();

    // Houd de eigen cart-modal van het thema weg van boven op Smart Cart.
    event.stopPropagation();

    openWhenReady();
  };

  // A cart link clicked before this file finished loading was held by the
  // inline guard — honour it now.
  if (config.pendingOpen) {
    const pendingAt = config.pendingAt;

    config.pendingOpen = false;

    if (Date.now() - pendingAt <= QUEUED_OPEN_MAX_WAIT) openWhenReady();
  }
})();
